import { useEffect, useRef } from "react";

/* ───── AMBIENT PARTICLES (lightweight) ───── */
export function AmbientCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w, h, animId;
    const particles = [];
    const resize = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
    resize();
    const colors = ["230,0,0", "255,80,60", "200,50,30"];
    for (let i = 0; i < 25; i++) particles.push({ x: Math.random() * 2000, y: Math.random() * 20000, r: Math.random() * 2 + 0.5, a: Math.random() * 0.15 + 0.05, s: Math.random() * 0.3 + 0.1, p: Math.random() * Math.PI * 2, color: colors[i % colors.length] });
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach(p => {
        p.y -= p.s; p.p += 0.01;
        if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
        ctx.beginPath();
        ctx.arc(p.x % w, p.y % (h + 20), p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.a * (0.5 + Math.sin(p.p) * 0.5)})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} data-testid="ambient-canvas" className="fixed inset-0 z-0 pointer-events-none" style={{ opacity: 0.5, willChange: "transform" }} />;
}

/* ───── ROAD ANIMATION ───── */
export function RoadCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w, h, animId;
    const resize = () => { w = c.width = c.offsetWidth; h = c.height = c.offsetHeight; };
    resize();
    const cars = Array.from({ length: 5 }, () => ({ x: Math.random() * 2000, y: 0.3 + Math.random() * 0.4, speed: 0.5 + Math.random() * 1.2, size: 3 + Math.random() * 2, active: Math.random() > 0.4 }));
    let t = 0;
    const draw = () => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "rgba(30,30,30,0.3)";
      ctx.fillRect(0, h * 0.45, w, h * 0.1);
      for (let i = 0; i < 20; i++) {
        const dx = ((i * w / 10 + t * 60) % (w + 80)) - 40;
        ctx.fillStyle = "rgba(255,255,255,0.12)";
        ctx.fillRect(dx, h * 0.497, 25, 2);
      }
      cars.forEach(car => {
        car.x += car.speed;
        if (car.x > w + 50) car.x = -50;
        ctx.beginPath();
        ctx.arc(car.x, h * car.y, car.size, 0, Math.PI * 2);
        ctx.fillStyle = car.active ? "#E60000" : "rgba(255,255,255,0.2)";
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} data-testid="road-canvas" className="absolute inset-0 w-full h-full" style={{ opacity: 0.7 }} />;
}

/* ───── CANADIAN MAP ───── */
export function MapCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w, h, animId;
    const resize = () => { w = c.width = c.offsetWidth; h = c.height = c.offsetHeight; };
    resize();
    const cities = [
      { name: "Saskatoon", x: 0.32, y: 0.42, active: true },
      { name: "Regina", x: 0.34, y: 0.55, active: true },
      { name: "Toronto", x: 0.72, y: 0.65, active: false },
      { name: "Ottawa", x: 0.76, y: 0.52, active: false },
      { name: "Winnipeg", x: 0.45, y: 0.48, active: false },
      { name: "Calgary", x: 0.22, y: 0.45, active: false },
      { name: "Vancouver", x: 0.08, y: 0.5, active: false },
      { name: "Montreal", x: 0.8, y: 0.48, active: false },
      { name: "Halifax", x: 0.88, y: 0.58, active: false },
      { name: "Moncton", x: 0.85, y: 0.52, active: false },
      { name: "St. John's", x: 0.95, y: 0.45, active: false },
    ];
    let t = 0;
    const draw = () => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);
      // Saskatchewan glow
      const skX = w * 0.28, skY = h * 0.3, skW = w * 0.1, skH = h * 0.35;
      ctx.fillStyle = `rgba(230,0,0,${0.04 + Math.sin(t * 0.5) * 0.02})`;
      ctx.fillRect(skX, skY, skW, skH);
      ctx.strokeStyle = "rgba(230,0,0,0.2)";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(skX, skY, skW, skH);
      // LIVE badge
      ctx.fillStyle = "#E60000";
      const bW = 50, bH = 20, bX = skX + skW / 2 - bW / 2, bY = skY - 28;
      ctx.fillRect(bX, bY, bW, bH);
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.max(11, w * 0.009)}px 'Outfit',sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("LIVE", bX + bW / 2, bY + bH / 2);
      ctx.textBaseline = "alphabetic";
      // Routes
      cities.forEach((c1, i) => {
        cities.slice(i + 1).forEach(c2 => {
          const dx = c1.x - c2.x, dy = c1.y - c2.y;
          if (Math.sqrt(dx * dx + dy * dy) < 0.3) {
            ctx.beginPath();
            ctx.moveTo(c1.x * w, c1.y * h);
            ctx.lineTo(c2.x * w, c2.y * h);
            const active = c1.active && c2.active;
            ctx.strokeStyle = active ? "rgba(230,0,0,0.3)" : "rgba(255,255,255,0.06)";
            ctx.lineWidth = active ? 2 : 0.8;
            ctx.setLineDash(active ? [] : [4, 8]);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        });
      });
      // Cities (no shadowBlur — use radial gradient instead for glow)
      cities.forEach(city => {
        const cx = city.x * w, cy = city.y * h;
        if (city.active) {
          const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 18 + Math.sin(t * 2) * 4);
          g.addColorStop(0, "rgba(230,0,0,0.35)");
          g.addColorStop(1, "rgba(230,0,0,0)");
          ctx.fillStyle = g;
          ctx.fillRect(cx - 22, cy - 22, 44, 44);
        }
        ctx.beginPath();
        ctx.arc(cx, cy, city.active ? 5 : 3, 0, Math.PI * 2);
        ctx.fillStyle = city.active ? "#E60000" : "rgba(255,255,255,0.3)";
        ctx.fill();
        ctx.font = `600 ${Math.max(11, w * 0.009)}px 'Outfit',sans-serif`;
        ctx.textAlign = "center";
        ctx.fillStyle = city.active ? "#fff" : "rgba(255,255,255,0.45)";
        ctx.fillText(city.name, cx, cy - 12);
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} data-testid="community-map-canvas" className="absolute inset-0 w-full h-full" style={{ opacity: 0.85 }} />;
}

/* ───── NEON MAPLE LEAF (optimized — pre-render glow, no shadowBlur) ───── */
export function MapleLeafCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w, h, animId;
    const resize = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
    resize();
    // Official Canadian flag maple leaf path
    const officialPoints = [
      [-90, 2030], [-45, 1167], [-156, 1069], [-1015, 1220], [-899, 900],
      [-919, 827], [-1860, 65], [-1648, -34], [-1614, -113], [-1800, -685],
      [-1258, -570], [-1185, -608], [-1080, -855], [-657, -401], [-546, -458],
      [-750, -1510], [-423, -1321], [-332, -1348], [0, -2000], [332, -1348],
      [423, -1321], [750, -1510], [546, -458], [657, -401], [1080, -855],
      [1185, -608], [1258, -570], [1800, -685], [1614, -113], [1648, -34],
      [1860, 65], [919, 827], [899, 900], [1015, 1220], [156, 1069],
      [45, 1167], [90, 2030],
    ];
    const drawLeaf = (cx, cy, scale) => {
      ctx.beginPath();
      const s = scale / 2015;
      officialPoints.forEach((p, i) => {
        const px = cx + p[0] * s, py = cy + p[1] * s;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.closePath();
    };
    // Pre-render a glow texture on an offscreen canvas (avoids per-frame shadowBlur)
    let glowCanvas = null;
    const renderGlow = () => {
      glowCanvas = document.createElement("canvas");
      glowCanvas.width = w;
      glowCanvas.height = h;
      const gCtx = glowCanvas.getContext("2d");
      const cx = w * 0.5, cy = h * 0.5;
      const size = Math.min(w, h) * 0.35;
      // Draw multiple strokes with increasing size for glow effect
      for (let i = 8; i >= 1; i--) {
        drawLeaf.call(null, cx, cy, size * (1 + i * 0.03));
        // Can't call drawLeaf with gCtx, redraw manually
        gCtx.beginPath();
        const s = (size * (1 + i * 0.03)) / 2015;
        officialPoints.forEach((p, j) => {
          const px = cx + p[0] * s, py = cy + p[1] * s;
          if (j === 0) gCtx.moveTo(px, py); else gCtx.lineTo(px, py);
        });
        gCtx.closePath();
        gCtx.strokeStyle = `rgba(230, 0, 0, ${0.04 * (9 - i)})`;
        gCtx.lineWidth = 2 + i;
        gCtx.stroke();
      }
      // Main bright outline
      gCtx.beginPath();
      const s = size / 2015;
      officialPoints.forEach((p, j) => {
        const px = cx + p[0] * s, py = cy + p[1] * s;
        if (j === 0) gCtx.moveTo(px, py); else gCtx.lineTo(px, py);
      });
      gCtx.closePath();
      gCtx.strokeStyle = "rgba(230, 0, 0, 0.4)";
      gCtx.lineWidth = 2;
      gCtx.stroke();
      // Subtle fill
      gCtx.fillStyle = "rgba(230, 0, 0, 0.04)";
      gCtx.fill();
    };
    renderGlow();
    let t = 0;
    const draw = () => {
      t += 0.008;
      ctx.clearRect(0, 0, w, h);
      if (glowCanvas) {
        const pulse = 0.7 + Math.sin(t * 1.5) * 0.15;
        ctx.globalAlpha = pulse;
        ctx.drawImage(glowCanvas, 0, 0);
        ctx.globalAlpha = 1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    const onResize = () => { resize(); renderGlow(); };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={ref} data-testid="maple-leaf-canvas" className="fixed inset-0 z-[2] pointer-events-none" style={{ willChange: "transform" }} />;
}
